/**
 * CommonPopupContext — 범용 레이어(팝업/모달) 상태 관리
 *
 * ── 설정 ────────────────────────────────────────────────
 *
 * ```tsx
 * import { CommonPopupProvider } from '@publishing/context/CommonPopupContext';
 *
 * <CommonPopupProvider>
 *   <MyComponent />
 * </CommonPopupProvider>
 * ```
 *
 * ── 사용 ────────────────────────────────────────────────
 *
 * ```tsx
 * 'use client';
 * import { useCommonPopup } from '@publishing/context/CommonPopupContext';
 *
 * function MyComponent() {
 *   const { openLayer } = useCommonPopup();
 *
 *   const handleOpen = () => {
 *     openLayer('my-popup', close => <MyPopup onClose={close} />);
 *   };
 *
 *   return <button onClick={handleOpen}>팝업 열기</button>;
 * }
 * ```
 *
 * ── API ──────────────────────────────────────────────────
 *
 * openLayer(id, content)
 *   - id: 레이어 고유 식별자. 같은 id가 이미 열려 있으면 추가로 열지 않음. html 고유 속성과 다름.
 *   - content: (close: () => void) => ReactNode
 *   - close를 인자로 받아 팝업 JSX를 반환하는 함수. close는 이 레이어를 닫는 함수.
 *
 * Provider가 열린 레이어 공통 동작을 처리함:
 *   - body scroll lock
 *   - ESC 닫기
 *   - focus trap
 *   - 열릴 때 첫 focusable 요소로 포커스 이동, 닫힐 때 이전 포커스 복귀
 */
'use client';

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { useBodyScrollLock } from '@publishing/hooks/useBodyScrollLock';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { pushGTMEvent, toBtnName } from '@/shared/utils/gtm';

// 팝업 안에서 키보드로 이동 가능한 요소를 찾기 위한 CSS 선택자
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

interface CommonPopupContextType {
  openLayer: (
    id: string,
    content: (close: () => void) => ReactNode,
    className?: string,
    /**
     * GA btnname — 미지정 시 레이어 id. 오프너 문구가 id 와 다를 때만 넘긴다.
     * `false` 면 click_popupopen 을 보내지 않는다 — 오프너가 다른 이벤트(click_leadform 등)를 직접 push 할 때.
     */
    btnName?: string | false,
  ) => void;
}

interface LayerItem {
  id: string;
  content: (close: () => void) => ReactNode;
  className?: string;
}

export const CommonPopupContext = createContext<CommonPopupContextType | null>(null);

export function CommonPopupProvider({ children }: { children: ReactNode }) {
  const [layers, setLayers] = useState<LayerItem[]>([]);

  // 퍼블리싱 페이지 최상위 래퍼(.ff-mkt) 안으로 팝업을 렌더링해 스타일 스코프를 유지.
  // lazy useState로 한 번만 잡으면 클라이언트 진입 시 .ff-mkt 커밋 전이라 null
  // 팝업이 떠 있을 때만(=클릭 후, .ff-mkt가 DOM에 존재할 때) 매 렌더에서 새로 읽음.
  const portalTarget =
    layers.length > 0 && typeof window !== 'undefined'
      ? document.querySelector('.ff-mkt')
      : null;

  // setLayers 업데이터는 StrictMode 에서 두 번 실행될 수 있어 집계를 넣지 않는다.
  // 열린 목록 판정은 이 ref 로 한다.
  const layersRef = useRef<LayerItem[]>([]);
  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  // 같은 id가 이미 열려 있으면 중복 추가하지 않음
  const openLayer = useCallback((id: string, content: (close: () => void) => ReactNode, className?: string, btnName?: string | false) => {
    if (layersRef.current.some(layer => layer.id === id)) return;

    // 시트 row 30 — 퍼블리싱 공통 팝업은 오프너가 페이지 곳곳에 흩어져 있지만
    // 전부 이 함수를 지난다 (Button action={{type:'popup'}} 포함).
    if (btnName !== false) {
      pushGTMEvent(GTM_EVENT.POPUP_OPEN, { btnname: toBtnName(btnName ?? id) });
    }

    setLayers(prev => {
      if (prev.some(layer => layer.id === id)) return prev;
      return [...prev, { id, content, className }];
    });
  }, []);

  // 레이어가 하나라도 열려 있으면 body 스크롤 잠금
  useBodyScrollLock(layers.length > 0);

  // Provider 리렌더마다 새 객체가 생성되는 걸 막아 Context 소비자의 불필요한 리렌더 방지
  const contextValue = useMemo(() => ({ openLayer }), [openLayer]);

  const layerElements = layers.map((layer, index) => {
    const close = () => setLayers(prev => prev.filter(l => l.id !== layer.id));
    return (
      // 가장 마지막으로 쌓인 레이어만 ESC·포커스 제어의 대상이 됨
      <CommonPopupLayer key={layer.id} active={index === layers.length - 1} onClose={close} className={layer.className}>
        {layer.content(close)}
      </CommonPopupLayer>
    );
  });

  return (
    <CommonPopupContext.Provider value={contextValue}>
      {children}
      {layers.length > 0 && (
        portalTarget
          ? createPortal(layerElements, portalTarget)
          : layerElements
      )}
    </CommonPopupContext.Provider>
  );
}

/**
 * 팝업 레이어 내부인지 여부.
 *
 * 시트 row 27(`click_btn`)과 row 29(`click_popup`)는 속성이 같고 **위치만 다르다**.
 * 이 컨텍스트로 자동 분기해 호출부가 판정하지 않아도 되게 한다.
 */
const InPopupContext = createContext(false);

export function useIsInPopup() {
  return useContext(InPopupContext);
}

export function useCommonPopup() {
  const context = useContext(CommonPopupContext);
  if (!context) throw new Error('useCommonPopup must be used within CommonPopupProvider');
  return context;
}

// 개별 레이어의 접근성 동작(포커스 이동·복귀, ESC·Tab 처리)을 담당
function CommonPopupLayer({
  active,
  onClose,
  className,
  children,
}: {
  active: boolean;
  onClose: () => void;
  className?: string;
  children: ReactNode;
}) {
  const layerRef = useRef<HTMLDivElement>(null);
  // 팝업이 닫힐 때 원래 포커스 위치로 돌려주기 위해 저장
  const prevFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  // onClose가 매 렌더마다 새 참조로 전달되므로 ref로 안정화 — keydown effect 재실행 방지
  useLayoutEffect(() => { onCloseRef.current = onClose; });

  // 이 레이어가 최상단(active)이 될 때 팝업 안 첫 번째 요소로 포커스 이동
  useEffect(() => {
    if (!active) return;
    prevFocusRef.current = document.activeElement as HTMLElement | null;
    const firstFocusable = layerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  }, [active]);

  // 레이어가 DOM에서 제거될 때 이전 포커스 위치 복구
  useEffect(() => {
    return () => {
      prevFocusRef.current?.focus();
    };
  }, []);

  // ESC: 닫기 / Tab: 포커스가 팝업 밖으로 나가지 않도록 첫↔마지막 요소 사이에서 순환 (focus trap)
  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const layer = layerRef.current;
      if (!layer) return;

      const focusables = Array.from(layer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      // Shift+Tab: 첫 요소에서 마지막 요소로 역방향 순환
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      // Tab: 마지막 요소에서 첫 요소로 순환
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active]);

  return (
    <InPopupContext.Provider value={true}>
      <div ref={layerRef} className={className}>
        {children}
      </div>
    </InPopupContext.Provider>
  );
}
